import deepmerge from 'deepmerge'
import logger from './log'
import reportError from './report-error'
import { redirectToLocation } from './redirect'
import { CSRF_PROTECTION_HEADER_NAME, CSRF_PROTECTION_HEADER_VALUE } from './constants'
import { MIME_TYPES } from './mime-types'
import { getFileNameFromContentDispositionHeader } from './http-client-util'

const authenticatePattern = /FormBased url="([^"]*)"/

// Header field set by WASAPI and other backend servers.
// It indicates the youngest modification timestamp of any element in a collection.
export const LAST_MODIFIED_HEADER = 'x-youngest-last-modified'
// Query parameter holding the latest modification timestamp seen by the scheduler.
// The parameter is used by the differential GET requests of the backend servers.
export const TIME_QUERY_PARAMETER = 'lastModified-gt'

const CONTENT_TYPE_HEADER = 'Content-Type'
const CONTENT_LENGTH_HEADER = 'Content-Length'
const CONTENT_DISPOSITION_HEADER = 'Content-Disposition'

export const http = { get, diff, post, put, del, patch }

/**
 * @typedef {Object} HttpResponse
 * @property {string} contentType - The content type ('json' or 'binary')
 * @property {object|blob} data - The response payload (as json or blob)
 * @property {object} customHeaders - Custom response headers (prefixed by 'x-...')
 * @property {number} time - The youngest modification time of the fetched resource if the http response contained a
 *                           LAST_MODIFIED_HEADER, otherwise the time when the response was received.
 *                           Intended to be used in conjunction with differential get requests to provide a time offset
 *                           for subsequent differential get requests.
 */

/**
 * Simple http get abstraction
 * @param url the url to get
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>|null} a promise, which is resolved to a HttpResponse object
 */
function get(url, options) {
    return fetchData(url, 'get', options)
}

/**
 * Differential get. The server delivers only results younger than the passed modified timestamp
 * @param url the url to get
 * @param modified the modified timestamp (pass the modified timestamp of the previous request or 0)
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>} a promise, which is resolved with a HttpResponse object extended with a
 *         'time' field (the youngest modification time of any result record)
 */
function diff(url, modified, options) {
    let queryStringParamsSeparator = '?'
    if (url.includes('?')) {
        queryStringParamsSeparator = '&'
    }

    const extUrl = `${url}${queryStringParamsSeparator}${TIME_QUERY_PARAMETER}=${modified}`
    return fetchData(extUrl, 'get', options)
}

/**
 * Simple http post abstraction
 * @param url the url to post to
 * @param payload the data (json or blob) to be posted
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>|null} a promise, which is resolved a HttpResponse object
 */
function post(url, payload, options) {
    return fetchData(url, 'post', options, payload)
}

/**
 * Simple http put abstraction
 * @param url the url to put to
 * @param payload the data (json or blob) to be put
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>|null} a promise, which is resolved to a HttpResponse object
 */
function put(url, payload, options) {
    return fetchData(url, 'put', options, payload)
}

/**
 * Simple http delete abstraction
 * @param url the url to the resource to be deleted
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>|null} a promise, which is resolved to a HttpResponse object
 */
function del(url, options) {
    return fetchData(url, 'delete', options)
}

/**
 * Simple http patch abstraction
 * @param url the url to send the patch to
 * @param payload the data (json or blob) to be put
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @return {Promise<HttpResponse>|null} a promise, which is resolved to a HttpResponse object
 */
function patch(url, payload, options) {
    return fetchData(url, 'PATCH', options, payload)
}

/**
 * Basic abstraction over fetch API that sets common headers and parses the response data
 * @param url the fetch url
 * @param method the http request method (optional, defaults to get)
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @param payload the data (json or blob) to be sent (optional, only needed for post and put operations)
 * @return {Promise<HttpResponse>|null} a promise, which is resolve to a HttpResponse object
 */
async function fetchData(url, method, options, payload) {
    const { redirectOptions } = options || {}
    if (payload && redirectOptions) {
        throw new Error('Parameters payload and redirectOptions can not occur simultaneously.')
    }
    try {
        const response = await fetchBasic(url, method, options, payload)
        const responseWithStatus = checkStatus(response, redirectOptions)
        return await parseData(responseWithStatus)
    } catch (error) {
        if ((!error.status || error.status !== 401) && error.code !== DOMException.ABORT_ERR) {
            reportError(error)
        }
        throw error
    }
}

/**
 * Basic abstraction over fetch API that sets common headers and triggers authentication if needed
 * @param url the fetch url
 * @param method the http request method (optional, defaults to get)
 * @param options additional/overwriting options for the underlying fetch method
 * @property options.abortController - an AbortController whose signal is added to the fetch options to make the request abortable
 * @param payload the data (json or blob) to be sent (optional, only needed for post and put operations)
 * @return a promise, that is resolved to a fetch response object
 */
function fetchBasic(url, method, options, payload) {
    let fetchOptions = {
        mode: 'cors',
        credentials: 'same-origin',
        method: method,
        headers: {
            accept: 'application/json'
        }
    }

    if (options) {
        fetchOptions = deepmerge(fetchOptions, options, { clone: false })

        if (options.abortController instanceof AbortController) {
            if (options.signal) {
                logger.warn(
                    'The `abortController` option was used and the signal for the fetch call will be replaced by the signal of the provided AbortController. Provide an own `abortController` option to make this request abortable.'
                )
            }

            fetchOptions.signal = options.abortController.signal
            delete fetchOptions.abortController
        }
    }

    if (payload) {
        if (payload instanceof FormData) {
            delete fetchOptions.headers['Accept']
            fetchOptions.body = payload
        } else {
            fetchOptions.headers[CONTENT_TYPE_HEADER] = 'application/json'
            fetchOptions.body = JSON.stringify(payload)
        }
    }

    // Set the token to the request only if exists in the store to avoid the header and give ATAPI the chance to reject
    // the request, if needed
    fetchOptions.headers[CSRF_PROTECTION_HEADER_NAME] = CSRF_PROTECTION_HEADER_VALUE

    return fetch(url, fetchOptions)
}

/**
 * Called by fetchBasic to check the response http status and throw an error if it is >= 300
 * @param response the fetch response object
 * @param redirectOptions additional/overwriting options for the underlying fetch method
 * @return the fetch response object
 */
function checkStatus(response, redirectOptions) {
    const status = response.status
    if (status === 401) {
        const authenticateHeader = response.headers.get('WWW-Authenticate')
        const matchResult = authenticatePattern.exec(authenticateHeader)
        if (matchResult) {
            const authenticateUrl = matchResult[1]
            redirectToLocation(authenticateUrl, 'sameWindow')
            return null
        } else {
            throw createError(response)
        }
    }
    // Http Status code Gone special case to handle redirect aproach in ajax call.
    else if (status === 410) {
        const windowHandle = redirectToLocation(response.headers.get('Location'), redirectOptions, response)
        return Object.assign(response, { windowHandle })
    } else if (status >= 200 && status < 300) {
        return response
    } else {
        throw createError(response)
    }
}

/**
 * Extract custom header from response. It interprets headers starting with 'x-' as custom header.
 * @param response a response object as returned by `fetch`
 * @returns {object} a simplified map with header name => value pairs (both strings). This method currently doesn't
 *                   support multiple values per header.
 */
function extractCustomHeaders(response) {
    const result = {}
    response.headers.forEach((value, name) => {
        const lcName = name.toLowerCase()
        if (lcName.startsWith('x-')) {
            result[lcName] = value
        }
    })
    return result
}

/**
 * Called by fetchData to extract the json or blob data from response object
 * @param response the fetch response object
 * @return {Promise<HttpResponse>|null} a promise, which is resolved to a HttpResponse object
 */
async function parseData(response) {
    if ((response.status >= 200 && response.status < 300) || response.status === 410) {
        const contentType = response.headers.get(CONTENT_TYPE_HEADER)
        const contentLength = parseInt(response.headers.get(CONTENT_LENGTH_HEADER))
        const customHeaders = extractCustomHeaders(response)
        const responseDate = Date.parse(response.headers.get('date'))
        const time = customHeaders[LAST_MODIFIED_HEADER]
        if (contentLength === 0 || response.status === 204) {
            return {
                customHeaders,
                responseDate,
                time
            }
        } else if (contentType && contentType.match(/json/)) {
            const data = await response.json()
            return {
                contentType: MIME_TYPES.JSON,
                data,
                customHeaders,
                responseDate,
                time
            }
        } else if (response.status === 410) {
            const data = response.windowHandle
            return {
                contentType: 'redirect',
                data,
                customHeaders,
                responseDate,
                time
            }
        } else {
            // The node-fetch library doesn't support standard response.blob() or response.arrayBuffer() methods.
            // Therefore response.buffer() is used for unit tests.
            const fileName = getFileNameFromContentDispositionHeader(response.headers.get(CONTENT_DISPOSITION_HEADER))
            const data = response.blob ? await response.blob() : await response.buffer()
            return {
                contentType: MIME_TYPES.BINARY,
                data,
                customHeaders,
                responseDate,
                time,
                attachmentFileName: fileName
            }
        }
    } else {
        return null
    }
}

/**
 * Called by checkStatus to create error object upon action failure
 * @param response  the fetch response object
 * @return an error object containing error status code, status text and error reasons as promise resolving to json
 */
function createError(response) {
    const contentType = response.headers.get(CONTENT_TYPE_HEADER)
    const error = new Error(`Error fetching URL ${response.url}: ${response.status} ${response.statusText}`)
    error.status = response.status
    error.reasons = contentType && contentType.match(/json/) ? response.json() : Promise.resolve()
    return error
}
